import {
    Activity,
    BadgeCheck,
    BookOpen,
    CreditCard,
    FileText,
    Fingerprint,
    FlaskConical,
    Key,
    KeyRound,
    LifeBuoy,
    Link as LinkIcon,
    Mail,
    MessageCircle,
    Package,
    PiggyBank,
    Rocket,
    Send,
    SquarePlay,
    Store,
    Ticket,
    Users,
    UtensilsCrossed,
    Webhook,
    Zap,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import type { ComponentType } from "react";
import type { ZudokuConfig } from "zudoku";
import { Head, LandingPage, useAuth } from "zudoku/components";
import { Stepper } from "zudoku/ui/Stepper";
/*
    Zudoku ships a Mermaid renderer but registers nothing for MDX: pages using
    <Mermaid> rendered as an empty gap, which is why several diagrams written
    weeks ago have never been visible on the site.
*/
import { Mermaid } from "zudoku/mermaid";
import { Link, useLocation, useNavigate } from "zudoku/router";

type IconType = ComponentType<{ size?: number | string; className?: string }>;

/*
    Committed OpenAPI specs, synced from kn-services
    (apps/api-gateway/openapi/) by ./scripts/sync-openapi.sh.

    These are FILES, not URLs, and that distinction is load-bearing.

    With `type: "url"` Zudoku cannot enumerate tags at build time, so it emits a
    DYNAMIC route (`/:tag?`). The prerenderer skips any route containing a
    `:param`, and Pagefind only indexes prerendered HTML — so the entire API
    reference was absent from the search index and the sitemap, and `serve.json`
    had to rewrite /en/api/** to /404.html to stop the SPA 404ing. Zudoku's own
    docs carry a :::danger block against `type: "url"`.

    With `type: "file"` every operation page is prerendered, indexed, crawlable,
    and the version selector becomes available.

    The specs are generated in kn-services and gated there by CI
    (scripts/check-openapi-drift.sh), so they cannot drift from the code.
*/
const OPENAPI_EN = "./openapi/openapi.json";
const OPENAPI_FR = "./openapi/openapi.fr.json";

/*
    Read a build-time public value.

    Docker always defines these (an unset build arg arrives as an empty string),
    and `??` would then hand the config "" instead of the default, producing a
    site whose links all point at nothing. Treat blank as absent so the only two
    outcomes are "a real override" or "the default".
*/
const envValue = (name: string): string | undefined => {
    const raw = process.env[name];
    return raw && raw.trim() !== "" ? raw.trim() : undefined;
};

// Validation build (`ZUDOKU_VALIDATE=1`, used by the pre-push hook + PR gate).
// Now that the specs are committed files, validation exercises the API reference
// as well as the MDX pages, with no secrets and no network.
const VALIDATE = process.env.ZUDOKU_VALIDATE === "1";

// Where the "Sign in" button sends people (Kwik Nkap dashboard / Keycloak login).
const DASHBOARD_URL =
    envValue("ZUDOKU_PUBLIC_DASHBOARD_URL") ?? "https://app.kwiknkap.com";

// The public API base URL shown in the docs. Differs per environment, so it is
// loaded from the env (resolved from 1Password) rather than hard-coded in MDX.
const API_BASE_URL =
    envValue("ZUDOKU_PUBLIC_API_BASE_URL") ?? "https://api.kwiknkap.com/v1";

// The gateway host (no /v1) — the OpenAPI spec's paths already include the /v1
// prefix, and the spec ships no `servers`, so the interactive playground needs
// an explicit server. Use the env host so "Try it" hits localhost on local,
// staging on staging, etc. (not the production URL).
const API_HOST = API_BASE_URL.replace(/\/v1\/?$/, "");

// The marketing/landing site, reused across the docs (help links, footer, etc.).
const LANDING_URL =
    envValue("ZUDOKU_PUBLIC_LANDING_URL") ?? "https://kwiknkap.com";

// Canonical origin of THIS docs site. Required for sitemap.xml: without it
// Zudoku emits no sitemap at all, so nothing here is discoverable by search
// engines regardless of how well it prerenders.
const DOCS_URL = envValue("ZUDOKU_PUBLIC_DOCS_URL") ?? "https://docs.kwiknkap.com";

/*
    Keycloak OIDC for the docs site.

    Same realm as the merchant portal (shared ISSUER), but this MUST be a
    dedicated PUBLIC client with PKCE — not the portal's client. The portal is a
    confidential client with a secret held server-side behind its BFF; a static
    docs site cannot hold a secret, and Keycloak refuses a browser PKCE flow for
    a confidential client. The docs client also needs its own redirect URI
    (<docs origin>/oauth/callback) registered.

    Unset issuer/clientId simply disables auth: the header falls back to the
    plain "Sign in" link, so a misconfigured environment degrades rather than
    breaking the site.
*/
const KEYCLOAK_ISSUER = envValue("ZUDOKU_PUBLIC_KEYCLOAK_ISSUER");
const KEYCLOAK_CLIENT_ID = envValue("ZUDOKU_PUBLIC_KEYCLOAK_CLIENT_ID");
const AUTH_ENABLED = Boolean(KEYCLOAK_ISSUER && KEYCLOAK_CLIENT_ID);

// The hosted checkout / payment pages (storefronts, events, payment links) live
// on the payment-portal — a DIFFERENT host from the API (they share the host only
// in production). Env-driven so local resolves to the payment-portal dev port.
const CHECKOUT_URL =
    envValue("ZUDOKU_PUBLIC_CHECKOUT_URL") ?? "https://pay.kwiknkap.com";

// ---------------------------------------------------------------------------
// Path-based bilingual docs (English + French), Fapshi-style (/en, /fr).
// Zudoku 0.82 has no native i18n, so we wire it ourselves:
//  - pages live under pages/en/** and pages/fr/** -> routes /en/... and /fr/...
//  - a NavigationPlugin returns the LOCALE + SECTION aware sidebar for the path.
//  - a custom horizontal tab bar (Guides / Getting started / API reference) sits
//    in the header; it is locale-aware and highlights the active section.
//  - a flag language dropdown + a "Sign in" button sit in the top-right header.
// Only our authored MDX + nav are translated; Zudoku's own chrome and the
// auto-generated API-reference labels stay English.
// ---------------------------------------------------------------------------

type Locale = "en" | "fr";

const LOCALES: { code: Locale; label: string; flag: string }[] = [
    { code: "en", label: "English", flag: "🇬🇧" },
    { code: "fr", label: "Français", flag: "🇫🇷" },
];

const localeFromPath = (path: string): Locale =>
    path.startsWith("/fr") ? "fr" : "en";

const swapLocale = (path: string, target: Locale): string => {
    const parts = path.split("/");
    if (parts[1] === "en" || parts[1] === "fr") parts[1] = target;
    else parts.splice(1, 0, target);
    return parts.join("/") || `/${target}`;
};

// -------------------------- sections (the top tabs) ------------------------

type SectionKey = "docs" | "api";

// Display order (left -> right in the tab bar).
//
// TWO tabs, not seven. Seven top-level tabs read as a table of contents that
// someone forgot to organise: the reader has to scan a horizontal list to find
// out where anything lives, and every tab switch replaces the sidebar wholesale
// so they lose their place.
//
// The split that matters is the one between prose you read and reference you
// look things up in — they have different shapes, different sources of truth
// and different reading patterns. Everything else is a group INSIDE the guides
// sidebar, where it is visible at a glance and stays put while you move around.
const SECTIONS: {
    key: SectionKey;
    label: Record<Locale, string>;
    landing: string; // slug relative to the locale, e.g. "introduction"
}[] = [
    {
        key: "docs",
        label: { en: "Documentation", fr: "Documentation" },
        landing: "introduction",
    },
    {
        key: "api",
        label: { en: "API reference", fr: "Référence API" },
        landing: "api",
    },
];

const pathAfterLocale = (path: string): string => {
    const parts = path.split("/");
    if (parts[1] === "en" || parts[1] === "fr") parts.splice(1, 1);
    return parts.join("/") || "/";
};

// Resolve which section the current path belongs to (specific matches first,
// otherwise the Guides section).
const sectionFromPath = (path: string): SectionKey => {
    // Only the generated reference has its own sidebar; everything else shares
    // the guides one, so a reader never loses the map by following a link.
    return /^\/api(\/|$)/.test(pathAfterLocale(path)) ? "api" : "docs";
};

// -------------------------- locale-aware sidebar ---------------------------

// A NavigationPlugin's getNavigation returns RESOLVED items, so doc items need an
// explicit URL `path` + `label` (no string shorthands).
type Badge = {
    label: string;
    color:
        | "green"
        | "blue"
        | "yellow"
        | "red"
        | "purple"
        | "indigo"
        | "gray"
        | "outline";
};
const doc = (slug: string, label: string, icon?: IconType, badge?: Badge) => ({
    type: "doc" as const,
    file: slug,
    path: `/${slug}`,
    label,
    ...(icon ? { icon } : {}),
    ...(badge ? { badge } : {}),
});
const link = (to: string, label: string, icon?: IconType, badge?: Badge) => ({
    type: "link" as const,
    to,
    label,
    ...(icon ? { icon } : {}),
    ...(badge ? { badge } : {}),
});
// A sidebar group. collapsible:false renders a non-collapsing section header with
// its items always visible; collapsible:true makes it an accordion.
const group = (
    label: string,
    items: unknown[],
    opts?: { collapsible?: boolean; collapsed?: boolean; icon?: IconType },
) => ({
    type: "category" as const,
    label,
    collapsible: opts?.collapsible ?? false,
    collapsed: opts?.collapsed ?? false,
    ...(opts?.icon ? { icon: opts.icon } : {}),
    items: items as never,
});

// Every page, with bilingual sidebar labels and a lucide icon.
//
// Flat rather than grouped by section: a page's label and icon do not change
// when it moves between sections, and keeping one list means a rename cannot
// leave two sections disagreeing about what a page is called.
const PAGES: { slug: string; en: string; fr: string; icon: IconType }[] = [
    // ---- Get started -------------------------------------------------------
    { slug: "introduction", en: "Introduction", fr: "Introduction", icon: BookOpen },
    { slug: "getting-started", en: "Create your account", fr: "Créer votre compte", icon: Rocket },
    { slug: "payments/api-keys", en: "Get your API keys", fr: "Obtenir vos clés API", icon: Key },
    {
        slug: "payments/authentication",
        en: "Authentication",
        fr: "Authentification",
        icon: KeyRound,
    },
    {
        slug: "build-with-ai",
        en: "Build with an AI agent",
        fr: "Développer avec un agent IA",
        icon: SquarePlay,
    },

    // ---- Concepts ----------------------------------------------------------
    {
        slug: "concepts/how-payments-work",
        en: "How a payment works",
        fr: "Comment fonctionne un paiement",
        icon: Activity,
    },
    {
        slug: "concepts/payment-states",
        en: "Payment states",
        fr: "États d'un paiement",
        icon: SquarePlay,
    },
    {
        slug: "concepts/environments-and-keys",
        en: "Environments & API keys",
        fr: "Environnements et clés API",
        icon: FlaskConical,
    },
    {
        slug: "concepts/scopes",
        en: "Scopes & least privilege",
        fr: "Portées et moindre privilège",
        icon: Fingerprint,
    },
    { slug: "concepts/idempotency", en: "Idempotency", fr: "Idempotence", icon: BadgeCheck },
    {
        slug: "concepts/errors",
        en: "Errors & failure codes",
        fr: "Erreurs et codes d'échec",
        icon: LifeBuoy,
    },

    // ---- Collections -------------------------------------------------------
    {
        slug: "collections/overview",
        en: "Choosing how to collect",
        fr: "Choisir son encaissement",
        icon: CreditCard,
    },
    { slug: "payments/overview", en: "Direct charge", fr: "Débit direct", icon: Send },
    {
        slug: "products/payment-links",
        en: "Payment links",
        fr: "Liens de paiement",
        icon: LinkIcon,
    },
    { slug: "products/invoices", en: "Invoices", fr: "Factures", icon: FileText },
    { slug: "products/storefronts", en: "Storefronts", fr: "Boutiques", icon: Store },
    {
        slug: "products/events",
        en: "Events & ticketing",
        fr: "Événements et billetterie",
        icon: Ticket,
    },

    // ---- Webhooks ----------------------------------------------------------
    {
        slug: "payments/webhooks",
        en: "Overview & events",
        fr: "Vue d'ensemble et événements",
        icon: Webhook,
    },
    {
        slug: "webhooks/delivery",
        en: "How delivery works",
        fr: "Comment fonctionne la livraison",
        icon: Activity,
    },

    // ---- Testing -----------------------------------------------------------
    {
        slug: "payments/testing",
        en: "Sandbox & failures",
        fr: "Bac à sable et échecs",
        icon: FlaskConical,
    },

    // ---- Resources ---------------------------------------------------------
    { slug: "products/inventory", en: "Inventory", fr: "Inventaire", icon: Package },
    { slug: "products/savings", en: "Savings", fr: "Épargne", icon: PiggyBank },
    { slug: "products/kyc", en: "Identity & KYC", fr: "Identité et KYC", icon: BadgeCheck },
    { slug: "products/kwik-id", en: "Kwik ID", fr: "Kwik ID", icon: Fingerprint },
    { slug: "products/nudle", en: "Nudle", fr: "Nudle", icon: UtensilsCrossed },
];

// Flat index of every page so the grouped sidebar can pull a page's bilingual
// label + icon by slug.
const ALL_PAGES = PAGES;
const page = (slug: string) =>
    ALL_PAGES.find((p) => p.slug === slug) ?? {
        slug,
        en: slug,
        fr: slug,
        icon: undefined as unknown as IconType,
    };
const d = (l: Locale, slug: string, badge?: Badge) => {
    const p = page(slug);
    return doc(`${l}/${p.slug}`, p[l], p.icon, badge);
};
const t = (l: Locale, en: string, fr: string) => (l === "fr" ? fr : en);

const POST: Badge = { label: "POST", color: "blue" };
const GET: Badge = { label: "GET", color: "green" };
const HOOK: Badge = { label: "HOOK", color: "purple" };

// Introduction renders as a clickable category whose sub-items jump to the page's
// own sections (anchor slugs differ per locale because the headings are translated).
const INTRO_SECTIONS: {
    en: string;
    fr: string;
    enSlug: string;
    frSlug: string;
}[] = [
    // Anchor slugs are derived from the rendered headings, so they must be
    // updated whenever introduction.mdx is rewritten — a stale slug scrolls
    // nowhere and gives no error.
    {
        en: "The platform",
        fr: "La plateforme",
        enSlug: "what-the-platform-does",
        frSlug: "ce-que-fait-la-plateforme",
    },
    {
        en: "What you can build",
        fr: "Ce que vous pouvez construire",
        enSlug: "what-you-can-build-today",
        frSlug: "ce-que-vous-pouvez-construire-aujourdhui",
    },
    {
        en: "Base URL",
        fr: "URL de base",
        enSlug: "base-url",
        frSlug: "url-de-base",
    },
    {
        en: "Authentication",
        fr: "Authentification",
        enSlug: "authentication",
        frSlug: "authentification",
    },
    {
        en: "Amounts",
        fr: "Montants",
        enSlug: "amounts",
        frSlug: "montants",
    },
];

const introNav = (l: Locale) => ({
    type: "category" as const,
    label: "Introduction",
    collapsible: false,
    icon: BookOpen,
    link: {
        type: "doc" as const,
        file: `${l}/introduction`,
        path: `/${l}/introduction`,
        label: "Introduction",
    },
    items: INTRO_SECTIONS.map((s) =>
        link(`/${l}/introduction#${l === "fr" ? s.frSlug : s.enSlug}`, l === "fr" ? s.fr : s.en),
    ) as never,
});

// Per-locale sidebar contents for each section.
//
// The guides sidebar carries the whole reading path as collapsible groups:
// Get started, Concepts, Collections, Webhooks, Testing, Resources. They are
// all visible at once, so the shape of the documentation is apparent without
// clicking, and moving between them does not swap the navigation out from
// under you.
//
// Groups after the first are collapsed by default. Six expanded groups is a
// wall of forty links; collapsed, the reader sees six labelled destinations and
// opens the one they want.
const SIDEBAR: Record<SectionKey, (l: Locale) => unknown[]> = {
    docs: (l) => [
        introNav(l),

        // The onboarding ladder, in the order you climb it.
        group(
            t(l, "Get started", "Démarrer"),
            [
                d(l, "getting-started"),
                d(l, "payments/api-keys"),
                d(l, "payments/authentication"),
                d(l, "build-with-ai"),
            ],
            { collapsible: true, icon: Rocket },
        ),

        // How the system behaves. No parameter lists here — those live in the
        // generated reference, and that separation is what stops the drift.
        group(
            t(l, "Concepts", "Concepts"),
            [
                d(l, "concepts/how-payments-work"),
                d(l, "concepts/payment-states"),
                d(l, "concepts/idempotency"),
                d(l, "concepts/errors"),
                d(l, "concepts/environments-and-keys"),
                d(l, "concepts/scopes"),
            ],
            { collapsible: true, collapsed: true, icon: Activity },
        ),

        // Money in. Every surface here settles through the same rail.
        group(
            t(l, "Collections", "Encaissements"),
            [
                d(l, "collections/overview"),
                d(l, "payments/overview"),
                d(l, "products/payment-links"),
                d(l, "products/invoices"),
                d(l, "products/storefronts"),
                d(l, "products/events"),
            ],
            { collapsible: true, collapsed: true, icon: CreditCard },
        ),

        group(
            t(l, "Webhooks", "Webhooks"),
            [d(l, "payments/webhooks"), d(l, "webhooks/delivery")],
            { collapsible: true, collapsed: true, icon: Webhook },
        ),

        group(t(l, "Testing", "Tests"), [d(l, "payments/testing")], {
            collapsible: true,
            collapsed: true,
            icon: FlaskConical,
        }),

        // The rest of the platform: real products, not part of the developer
        // API today. Present so they are findable, last so they do not clutter
        // the integration path.
        group(
            t(l, "More products", "Autres produits"),
            [
                d(l, "products/inventory"),
                d(l, "products/savings"),
                d(l, "products/kyc"),
                d(l, "products/kwik-id"),
                d(l, "products/nudle"),
            ],
            { collapsible: true, collapsed: true, icon: Package },
        ),
    ],

    // The generated reference owns its own sidebar; see localeNavigationPlugin.
    api: (l) => [
        group(t(l, "Endpoints", "Points de terminaison"), [
            link(`/${l}/api/direct-payments`, t(l, "Direct payments", "Paiements directs"), Send),
            link(`/${l}/api/payment-links`, t(l, "Payment links", "Liens de paiement"), LinkIcon),
            link(`/${l}/api/invoices`, t(l, "Invoices", "Factures"), FileText),
            link(`/${l}/api/files`, t(l, "Files", "Fichiers"), Package),
        ]),
    ],
};

const localeNavigationPlugin = {
    getRoutes: () => [],
    getNavigation: async (path: string) => {
        // On the interactive reference (/<locale>/api*), let the OpenAPI plugin own
        // the sidebar (its live operations + Try it). Returning our prose nav here
        // too would duplicate the endpoints. On every other page, show our nav.
        if (/^\/api(\/|$)/.test(pathAfterLocale(path))) return [] as never;
        const l = localeFromPath(path);
        const section = sectionFromPath(path);
        return SIDEBAR[section](l) as never;
    },
};

// -------------------------- header: tabs + controls ------------------------

// Full-width horizontal tab row, rendered below the header (Fapshi-style).
const SectionTabs = () => {
    const { pathname } = useLocation();
    const l = localeFromPath(pathname);
    const active = sectionFromPath(pathname);
    return (
        <div className="sticky top-(--top-header-height) z-20 w-full border-b border-border bg-background/80 backdrop-blur">
            <nav className="max-w-screen-2xl mx-auto flex items-center gap-7 px-4 lg:px-8 h-11 text-sm">
                {SECTIONS.map((s) => {
                    const isActive = s.key === active;
                    return (
                        <Link
                            key={s.key}
                            to={`/${l}/${s.landing}`}
                            className={`relative flex h-full items-center font-medium transition-colors ${
                                isActive
                                    ? "text-foreground after:absolute after:-bottom-px after:left-0 after:right-0 after:h-0.5 after:bg-[#0DC9AC] after:rounded-full"
                                    : "text-foreground/60 hover:text-foreground"
                            }`}
                        >
                            {s.label[l]}
                        </Link>
                    );
                })}
            </nav>
        </div>
    );
};

const LanguageSwitcher = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    const current = localeFromPath(location.pathname);
    const active = LOCALES.find((x) => x.code === current) ?? LOCALES[0];

    useEffect(() => {
        if (!open) return;
        const onClick = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node))
                setOpen(false);
        };
        const onKey = (e: KeyboardEvent) =>
            e.key === "Escape" && setOpen(false);
        document.addEventListener("mousedown", onClick);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onClick);
            document.removeEventListener("keydown", onKey);
        };
    }, [open]);

    const choose = (code: Locale) => {
        setOpen(false);
        if (code !== current) navigate(swapLocale(location.pathname, code));
    };

    return (
        <div ref={ref} className="relative">
            <button
                type="button"
                aria-haspopup="listbox"
                aria-expanded={open}
                onClick={() => setOpen((v: boolean) => !v)}
                className="flex items-center gap-1.5 rounded-lg border border-neutral-200 dark:border-neutral-800 px-2.5 py-1.5 text-sm font-medium text-neutral-700 dark:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
            >
                <span className="text-base leading-none">{active.flag}</span>
                <span className="hidden sm:inline">
                    {active.code.toUpperCase()}
                </span>
                <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className={`transition-transform duration-200 ${open ? "rotate-180" : ""}`}
                >
                    <path d="m6 9 6 6 6-6" />
                </svg>
            </button>
            <div
                role="listbox"
                className={`absolute right-0 mt-2 w-44 origin-top-right rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 shadow-lg p-1 z-50 transition duration-150 ease-out ${
                    open
                        ? "opacity-100 scale-100 translate-y-0"
                        : "opacity-0 scale-95 -translate-y-1 pointer-events-none"
                }`}
            >
                {LOCALES.map((loc) => {
                    const selected = loc.code === current;
                    return (
                        <button
                            type="button"
                            key={loc.code}
                            role="option"
                            aria-selected={selected}
                            onClick={() => choose(loc.code)}
                            className={`flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm transition-colors ${
                                selected
                                    ? "bg-neutral-100 dark:bg-neutral-800 font-semibold"
                                    : "hover:bg-neutral-100 dark:hover:bg-neutral-800"
                            }`}
                        >
                            <span className="text-base leading-none">
                                {loc.flag}
                            </span>
                            <span className="flex-1 text-left">
                                {loc.label}
                            </span>
                            {selected && (
                                <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    className="text-emerald-500"
                                >
                                    <path d="M20 6 9 17l-5-5" />
                                </svg>
                            )}
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

const BTN =
    "inline-flex items-center gap-1.5 rounded-md bg-[#0A8478] hover:bg-[#0bb39a] px-3.5 py-1.5 text-sm font-semibold text-white shadow-sm transition-colors";

const ArrowRight = () => (
    <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
);

/*
    Signed out -> "Sign in". Signed in -> who you are, and a direct route to the
    dashboard.

    Showing the signed-in identity here is what makes the docs feel part of the
    product rather than a separate site, and the dashboard link is the shortest
    path from reading about a thing to doing it. With auth disabled (no Keycloak
    env) this degrades to the previous plain link.
*/
const SignInButton = () => {
    const { pathname } = useLocation();
    const fr = localeFromPath(pathname) === "fr";
    const auth = useAuth();

    if (auth.isAuthEnabled && auth.isAuthenticated) {
        const who = auth.profile?.name ?? auth.profile?.email ?? (fr ? "Compte" : "Account");
        return (
            <div className="flex items-center gap-2">
                <span
                    className="hidden lg:inline max-w-[14rem] truncate text-sm text-foreground/70"
                    title={who}
                >
                    {who}
                </span>
                <a href={DASHBOARD_URL} target="_blank" rel="noreferrer" className={BTN}>
                    {fr ? "Tableau de bord" : "Go to dashboard"}
                    <ArrowRight />
                </a>
            </div>
        );
    }

    // Auth enabled but signed out: start the OIDC flow in place, so the reader
    // returns to the page they were on rather than landing on the dashboard.
    if (auth.isAuthEnabled) {
        return (
            <button type="button" onClick={() => void auth.login()} className={BTN}>
                {fr ? "Se connecter" : "Sign in"}
                <ArrowRight />
            </button>
        );
    }

    return (
        <a href={DASHBOARD_URL} target="_blank" rel="noreferrer" className={BTN}>
            {fr ? "Se connecter" : "Sign in"}
            <ArrowRight />
        </a>
    );
};

const HeaderControls = () => (
    <div className="flex items-center gap-2">
        <LanguageSwitcher />
        <SignInButton />
    </div>
);

// Small global CSS tweaks injected via a slot (Zudoku has no direct CSS config).
const GlobalStyles = () => (
    <style>{`
        /* Widen the header search box (Zudoku default is md:w-56 / 14rem). */
        @media (min-width: 768px) {
            header [class*="md:w-56"] { width: 28rem !important; max-width: 28rem !important; }
        }
        /* Keep the header (and its language dropdown) above the sticky section-tab
           bar; otherwise the header's stacking context (z-10) sits below the tab
           bar (z-20) and the dropdown gets clipped behind the page content. */
        header[class*="sticky"] { z-index: 40 !important; }
        /* Our custom section-tab row (44px) lives in the layout-after-head slot, so
           Zudoku doesn't know its height — and because config.navigation is empty,
           Zudoku's TopNavigation forces --top-nav-height to 0px. Report the real
           height here so --header-height (and the body height + scroll padding it
           drives) excludes the tab row instead of overflowing by 44px. */
        :root { --top-nav-height: 2.75rem !important; }
        /* Hide the "powered by Zudoku" footer badge. MIT requires the copyright
           notice in SOURCE distributions, not in rendered UI — the notice stays
           in node_modules and in our licence attribution, where the licence
           actually asks for it. */
        a[href*="zudoku.dev"] { display: none !important; }
    `}</style>
);

// Help & Support card pinned at the top of the sidebar (Fapshi-style). TODO: the
// support URLs/WhatsApp number below are sensible placeholders — confirm the real
// ones with the team.
const SUPPORT_LINKS: {
    icon: IconType;
    en: string;
    fr: string;
    descEn: string;
    descFr: string;
    href: string;
}[] = [
    {
        icon: LifeBuoy,
        en: "Help center",
        fr: "Centre d'aide",
        descEn: "Guides, FAQs and troubleshooting",
        descFr: "Guides, FAQ et dépannage",
        href: `${LANDING_URL}/support`,
    },
    {
        icon: Mail,
        en: "Email support",
        fr: "Support par e-mail",
        descEn: "support@kwiknkap.com",
        descFr: "support@kwiknkap.com",
        href: "mailto:support@kwiknkap.com",
    },
    {
        icon: MessageCircle,
        en: "WhatsApp",
        fr: "WhatsApp",
        descEn: "Chat with our team",
        descFr: "Discutez avec notre équipe",
        href: "https://wa.me/237600000000",
    },
    {
        icon: Users,
        en: "Developer community",
        fr: "Communauté de développeurs",
        descEn: "Build alongside other developers",
        descFr: "Échangez avec d'autres développeurs",
        href: `${LANDING_URL}/community`,
    },
];

const HelpSupport = () => {
    const { pathname } = useLocation();
    const l = localeFromPath(pathname);
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) return;
        const onClick = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node))
                setOpen(false);
        };
        const onKey = (e: KeyboardEvent) =>
            e.key === "Escape" && setOpen(false);
        document.addEventListener("mousedown", onClick);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onClick);
            document.removeEventListener("keydown", onKey);
        };
    }, [open]);

    return (
        <div ref={ref} className="relative px-3 pt-4 pb-1">
            <button
                type="button"
                aria-expanded={open}
                onClick={() => setOpen((v: boolean) => !v)}
                className="flex w-full items-center gap-3 rounded-xl border border-border bg-card p-3 text-left transition-colors hover:bg-muted/50"
            >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#0DC9AC]/10 text-[#0DC9AC]">
                    <LifeBuoy size={18} />
                </span>
                <span className="min-w-0 flex-1">
                    <span className="block text-sm font-semibold">
                        {l === "fr" ? "Aide et support" : "Help & Support"}
                    </span>
                    <span className="block truncate text-xs text-muted-foreground">
                        {l === "fr"
                            ? "Ressources et contact"
                            : "Resources and contact"}
                    </span>
                </span>
                <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className={`shrink-0 text-muted-foreground transition-transform duration-200 ${open ? "rotate-180" : ""}`}
                >
                    <path d="m6 9 6 6 6-6" />
                </svg>
            </button>
            {/* Absolute overlay so it floats over the sidebar nav instead of
                pushing the items down. */}
            <div
                className={`absolute inset-x-3 top-full z-30 origin-top transition-all duration-150 ease-out ${
                    open
                        ? "translate-y-0 opacity-100"
                        : "pointer-events-none -translate-y-1 opacity-0"
                }`}
            >
                <div className="rounded-xl border border-border bg-card p-1 shadow-xl">
                    {SUPPORT_LINKS.map((item) => (
                        <a
                            key={item.en}
                            href={item.href}
                            target="_blank"
                            rel="noreferrer"
                            className="flex items-center gap-3 rounded-lg p-2.5 transition-colors hover:bg-muted/60"
                        >
                            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-border text-muted-foreground">
                                <item.icon size={16} />
                            </span>
                            <span className="min-w-0">
                                <span className="block text-sm font-medium">
                                    {l === "fr" ? item.fr : item.en}
                                </span>
                                <span className="block truncate text-xs text-muted-foreground">
                                    {l === "fr" ? item.descFr : item.descEn}
                                </span>
                            </span>
                        </a>
                    ))}
                </div>
            </div>
        </div>
    );
};

// Renders above the sidebar nav. On the OpenAPI reference pages (which are
// generated, not MDX — so the BackToOverview MDX component can't reach them) it
// adds a link back to the API reference overview. Help & Support shows below it.
const SidebarTop = () => {
    const { pathname } = useLocation();
    const l = localeFromPath(pathname);
    const onApi = /^\/(en|fr)\/api(\/|$)/.test(pathname);
    return (
        <>
            {onApi && (
                <Link
                    to={`/${l}/payments/overview`}
                    className="mx-3 mt-4 flex items-center gap-1.5 rounded-lg border border-border bg-card px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted/50 hover:text-foreground"
                >
                    <svg
                        width="16" height="16" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                    >
                        <path d="m15 18-6-6 6-6" />
                    </svg>
                    {l === "fr"
                        ? "Référence API · Vue d'ensemble"
                        : "API reference · Overview"}
                </Link>
            )}
            <HelpSupport />
        </>
    );
};

// MDX component: renders the env-driven public API base URL as a styled code box,
// so the base URL shown in the docs is not hard-coded and tracks the environment.
const ApiBaseUrl = () => (
    <div className="not-prose my-4 overflow-x-auto rounded-lg border border-border bg-muted/40 px-4 py-3 font-mono text-sm">
        {API_BASE_URL}
    </div>
);

// MDX component: a back link to the API reference overview (locale-aware).
const BackToOverview = () => {
    const { pathname } = useLocation();
    const l = localeFromPath(pathname);
    return (
        <Link
            to={`/${l}/payments/overview`}
            className="not-prose mb-5 inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
        >
            <svg
                width="16" height="16" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
            >
                <path d="m15 18-6-6 6-6" />
            </svg>
            {l === "fr" ? "Référence API · Vue d'ensemble" : "API reference · Overview"}
        </Link>
    );
};

// Build-time remark plugin: rewrite the canonical production URLs written in the
// MDX (curl examples, prose, AND clickable links) to the env-resolved URLs, so
// every page tracks the environment it's served in — dev → localhost ports,
// staging → staging hosts, prod → prod. Applies to both text/code (node.value)
// and link/image hrefs (node.url).
// (The bare checkout host pay.kwiknkap.com — storefronts / events / payment links —
// is rewritten too, but AFTER the /v1 rule so the API base still wins for API
// URLs on the same host.)
const stripScheme = (url: string) => url.replace(/^https?:\/\//, "");
const URL_REWRITES: ReadonlyArray<readonly [string, string]> = [
    // Most specific first so hosts don't partially overlap during replacement.
    ["https://api.kwiknkap.com/v1", API_BASE_URL], // public API base (curl/examples)
    ["https://pay.kwiknkap.com", CHECKOUT_URL], // hosted checkout pages (store / events / payment-links)
    ["https://app.kwiknkap.com", DASHBOARD_URL], // dashboard / client portal link hrefs
    ["app.kwiknkap.com", stripScheme(DASHBOARD_URL)], // bare-host dashboard link text
    ["https://kwiknkap.com", LANDING_URL], // marketing / landing links
];
const rewriteCanonicalUrls = () => {
    const apply = (value: string) => {
        let out = value;
        for (const [from, to] of URL_REWRITES) {
            if (out.includes(from)) out = out.split(from).join(to);
        }
        return out;
    };
    type Node = { value?: string; url?: string; children?: unknown[] };
    const walk = (node: Node) => {
        if (typeof node.value === "string") node.value = apply(node.value);
        if (typeof node.url === "string") node.url = apply(node.url);
        if (Array.isArray(node.children)) node.children.forEach((c) => walk(c as Node));
    };
    return (tree: Node) => walk(tree);
};

const config: ZudokuConfig = {
    site: {
        title: "Kwik Nkap API",
        // Custom Kwik Nkap wordmark (same CDN asset the merchant-portal uses), with a
        // white variant for dark mode.
        logo: {
            src: {
                light: "https://kn-prod-store.fra1.cdn.digitaloceanspaces.com/img/logo/logotype_black.png",
                dark: "https://kn-prod-store.fra1.cdn.digitaloceanspaces.com/img/logo/logotype_white.png",
            },
            alt: "Kwik Nkap",
            width: "170px",
            href: "/en/introduction",
        },
    },
    metadata: {
        /*
            `%s` is the page title. Without it every page in the site shipped the
            SAME <title> — Zudoku's titleTemplate returns this string verbatim
            when a page has a title, so a template with no placeholder discards
            it. 64 identical titles is close to the worst thing you can do to a
            docs site in search: Google cannot tell the pages apart, and every
            result reads the same in the SERP.
        */
        title: "%s — Kwik Nkap",
        defaultTitle: "Kwik Nkap API — Developer Documentation",
        description:
            "Accept MTN MoMo and Orange Money payments in XAF — the Kwik Nkap payments API and platform documentation for builders in Cameroon.",
        favicon: "/kwik-nkap-icon.svg",
    },
    // Custom components usable in any .mdx page.
    mdx: {
        components: { ApiBaseUrl, BackToOverview, Head, LandingPage, Mermaid, Stepper },
    },
    // Rewrite the canonical base URL in MDX (curl examples, prose) to the
    // env-resolved one at build time, so every example tracks the environment.
    build: {
        remarkPlugins: [rewriteCanonicalUrls],
    },
    // Zudoku disables interactive security by DEFAULT (defaults.apis.disableSecurity
    // defaults to true). We turn it on so the playground surfaces the X-API-Key
    // scheme: a developer selects "X-API-Key" in the Try-it Authentication panel and
    // enters their key in the Authorize dialog. It also renders the per-operation
    // security requirement and the Security Schemes section on the API overview.
    defaults: {
        apis: {
            disableSecurity: false,
        },
    },
    // In VALIDATE mode the API reference is dropped (no spec fetch): we only need to
    // compile + prerender the authored MDX pages, which is what catches SSR/`{expr}`
    // errors. The real build keeps the live URL-sourced API reference.
    /*
        Always file-sourced, including in validation builds. Previously the
        VALIDATE branch set `apis: []`, so the PR gate compiled every MDX page
        but never exercised the API reference at all — the "committed offline
        stub" its comment referred to did not exist. Now it does, so validation
        covers the reference too.
    */
    apis: [
        { type: "file", input: OPENAPI_EN, path: "/en/api" },
        { type: "file", input: OPENAPI_FR, path: "/fr/api" },
    ],
    // Self-hosted full-text search (Cmd/Ctrl+K). Pagefind builds the index at
    // `zudoku build`; in dev the box is present and indexes the dev content.
    search: { type: "pagefind" },
    aiAssistants: ["claude", "chatgpt"],
    plugins: [localeNavigationPlugin],
    slots: {
        // Section tabs sit in their own full-width row BELOW the header; the language
        // dropdown + Sign in button sit at the top-right of the header row.
        "layout-before-head": <GlobalStyles />,
        "layout-after-head": <SectionTabs />,
        "head-navigation-end": <HeaderControls />,
        // Help & Support card pinned above the sidebar navigation.
        "navigation-before": <SidebarTop />,
    },
    // Emits sitemap.xml at build. Now that the API reference is prerendered
    // (type: "file"), its operation pages are included — previously they were
    // dynamic routes and could not appear here even if a sitemap existed.
    // Only registered when both values are present; see KEYCLOAK_ISSUER above.
    ...(AUTH_ENABLED
        ? {
              authentication: {
                  type: "openid" as const,
                  issuer: KEYCLOAK_ISSUER!,
                  clientId: KEYCLOAK_CLIENT_ID!,
              },
          }
        : {}),
    sitemap: { siteUrl: DOCS_URL, changefreq: "weekly" },
    redirects: [
        /*
            The hand-written endpoint pages were deleted: they duplicated the
            generated reference and were the reason the docs could contradict
            the code. These were public URLs, so they redirect into the
            generated reference rather than 404 -- dropping them silently would
            break external links and search rankings.
        */
        { from: "/en/payments/create-a-payment", to: "/en/api/direct-payments" },
        { from: "/fr/payments/create-a-payment", to: "/fr/api/direct-payments" },
        { from: "/en/payments/list-payments", to: "/en/api/direct-payments" },
        { from: "/fr/payments/list-payments", to: "/fr/api/direct-payments" },
        { from: "/en/payments/payment-status", to: "/en/api/direct-payments" },
        { from: "/fr/payments/payment-status", to: "/fr/api/direct-payments" },
        { from: "/en/payments/create-a-payment-link", to: "/en/api/payment-links" },
        { from: "/fr/payments/create-a-payment-link", to: "/fr/api/payment-links" },
        { from: "/en/payments/get-a-payment-link", to: "/en/api/payment-links" },
        { from: "/fr/payments/get-a-payment-link", to: "/fr/api/payment-links" },
        { from: "/en/payments/list-payment-links", to: "/en/api/payment-links" },
        { from: "/fr/payments/list-payment-links", to: "/fr/api/payment-links" },
        // Quickstart merged into the stepped Get started page — same content,
        // one path instead of two half-overlapping ones.
        { from: "/en/quickstart", to: "/en/getting-started" },
        { from: "/fr/quickstart", to: "/fr/getting-started" },
        { from: "/", to: "/en/introduction" },
        { from: "/en", to: "/en/introduction" },
        { from: "/fr", to: "/fr/introduction" },
    ],
    docs: {
        files: "/pages/**/*.{md,mdx}",
        /*
            Machine-readable docs.

            A growing share of integrations are written with an agent in the
            loop, and an agent that cannot read the docs guesses instead — which
            is how you get code that treats a 201 as paid. These make the site
            legible without scraping:

              /llms.txt       a map of the site, with a link to every page
              /llms-full.txt  every page's markdown in one file
              <page>.md       the markdown source of any single page

            Convention: https://llmstxt.org/
        */
        llms: { llmsTxt: true, llmsTxtFull: true },
    },
};

export default config;
